import React from 'react';

/**
 * ContactModal - A modal component for displaying contact information
 * Used to show driver/rider phone numbers for booked rides
 */
const ContactModal = ({ isOpen, onClose, contactName, contactPhone, contactType }) => {
  if (!isOpen) return null;

  // Format phone number for display (e.g., +15551234567 -> (555) 123-4567)
  const formatPhoneDisplay = (phone) => {
    if (!phone) return 'Not available';

    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');

    // Handle US numbers (10 or 11 digits)
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length === 11 && digits[0] === '1') {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }

    // Return original format for international numbers
    return phone;
  };

  // Create tel: link (keeps the + for international format)
  const getTelLink = (phone) => {
    if (!phone) return '#';
    return `tel:${phone}`;
  };

  const isDriver = contactType === 'driver';
  const title = isDriver ? 'Contact Driver' : 'Contact Rider';
  const buttonText = isDriver ? 'Call Driver' : 'Call Rider';
  const accentColor = isDriver ? 'green' : 'blue';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-sm w-full transform transition-all">
          {/* Header */}
          <div className={`px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-${accentColor}-50 rounded-t-xl`}>
            <h3 className={`text-lg font-semibold text-${accentColor}-800`}>
              {title}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            {/* Contact Name */}
            <div className="text-center mb-4">
              <div className={`w-16 h-16 mx-auto rounded-full bg-${accentColor}-100 flex items-center justify-center mb-3`}>
                <svg className={`w-8 h-8 text-${accentColor}-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h4 className="text-xl font-semibold text-gray-900">
                {contactName || (isDriver ? 'Your Driver' : 'Your Rider')}
              </h4>
            </div>

            {/* Phone Number */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-center gap-3">
                <svg className={`w-5 h-5 text-${accentColor}-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <a
                  href={getTelLink(contactPhone)}
                  className={`text-lg font-medium text-${accentColor}-600 hover:text-${accentColor}-700 hover:underline`}
                >
                  {formatPhoneDisplay(contactPhone)}
                </a>
              </div>
            </div>

            {/* Call Button */}
            <a
              href={getTelLink(contactPhone)}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold text-white bg-${accentColor}-600 hover:bg-${accentColor}-700 transition-colors`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              {buttonText}
            </a>
          </div>

          {/* Footer note */}
          <div className="px-6 pb-4">
            <p className="text-xs text-gray-500 text-center">
              Contact information is only shared for scheduled rides
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
